import {
  Form,
  FormLayout,
  TextField,
  Layout,
  Page,
  Button,
  ChoiceList,
  Tooltip,
  Icon,
  Text,
} from "@shopify/polaris";
import { useField, useForm, notEmpty, SubmitResult } from "@shopify/react-form";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import createApp from "@shopify/app-bridge";
import { getSessionToken } from "@shopify/app-bridge-utils";

import useMessage from "../../hooks/useMessage";
import { formSubmitHandler } from "../../utils/forms";
import { isValidEmail } from "../../utils/validation";
import { handleError } from "../../utils/errorHandling";
import { getAppConfig, getShop } from "../../utils/misc";
import { QuestionCircleIcon } from "@shopify/polaris-icons";
import {
  REQUIRED_FIELD_MESSAGE,
  INCORRECT_EMAIL_FORMAT_MESSAGE,
} from "../../constants/validation";
import styles from "./Installation.module.css";
import installationConfig from "./config";
import { StoreType } from "../Settings/components/types";
import { validateApiKey } from "../../requests";
const appConfig = getAppConfig();

function Installation() {
  const navigate = useNavigate();
  const { bannerMarkup, showMessage } = useMessage();
  const [shop, setShop] = useState("");

  useEffect(() => {
    const fetchShop = async () => {
      try {
        if (appConfig) {
          const app = createApp(appConfig);
          const token = await getSessionToken(app)
            .then((token: string) => token)
            .catch((err: unknown) => {
              handleError(err, showMessage, navigate);
              return "";
            });

          const shopDomain = getShop(token);
          setShop(shopDomain);
        }
      } catch (err) {
        handleError(err, showMessage, navigate);
      }
    };

    fetchShop();
  }, [showMessage, navigate]);

  const { fields, submit, submitting } = useForm({
    fields: {
      iterableKey: useField({
        value: "",
        validates: [notEmpty(REQUIRED_FIELD_MESSAGE)],
      }),
      email: useField({
        value: "",
        validates: [
          notEmpty(REQUIRED_FIELD_MESSAGE),
          isValidEmail(INCORRECT_EMAIL_FORMAT_MESSAGE),
        ],
      }),
      storeType: useField({
        value: StoreType.TRADITIONAL,
        validates: [],
      }),
    },
    async onSubmit(formValues): Promise<SubmitResult> {
      if (!shop) {
        throw new Error("Shop not available");
      }

      const config = {
        request: (payload: { iterableKey: string; email: string; storeType: StoreType }) => {
          return validateApiKey(shop, {
            iterable_api_key: payload.iterableKey,
            email: payload.email,
          });
        },
        redirectUrl: "/synchronizations/new",
        nextStepFields: ["iterableKey", "email", "storeType"],
      };

      return await formSubmitHandler(navigate, config, formValues, showMessage);
    },
  });

  return (
    <Page title="Installation" narrowWidth>
      <div className={styles.bannerContainer}>{bannerMarkup}</div>

      <Layout>
        <Layout.Section>
          <p>
            Please enter the API key of your Iterable project (must be a
            Server-side API Key) where all the Shopify events and users will be
            tracked. The email will only be used to send a message when the data
            backfill is complete (next screen).
          </p>
          <br />

          <Form noValidate preventDefault onSubmit={submit}>
            <FormLayout>
              <TextField
                name="iterableKey"
                label="Please insert your API Key"
                type="text"
                autoComplete="off"
                {...fields.iterableKey}
              />

              <TextField
                name="email"
                label="Please insert your Email"
                type="email"
                autoComplete="off"
                {...fields.email}
              />
  
              <div>
                <div className={styles.storeTypeTitleContainer}>
                  <Text as="span">Store type</Text>
                  <Tooltip content={installationConfig.storeTypeHelpText}>
                    <Icon source={QuestionCircleIcon} />
                  </Tooltip>
                </div>
                <ChoiceList
                  title=""
                  choices={installationConfig.storeTypeChoices}
                  selected={[fields.storeType.value]}
                  onChange={(selected: string[], name: string) =>
                    fields.storeType.onChange(selected[0] as StoreType)
                  }
                />
              </div>
              <div className={styles.nextButtonContainer}>
                <Button submit loading={submitting}>
                  Next
                </Button>
              </div>
            </FormLayout>
          </Form>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default Installation;
